import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Fuel, 
    Plus, 
    Eye, 
    Edit,
    Trash2,
    CheckCircle, 
    Clock, 
    Lock,
    Unlock,
    AlertTriangle,
    BarChart3,
    FileText,
    Download,
    Filter,
    Ship,
    MapPin,
    XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const statusLabels = {
    em_aberto: { label: 'Em Aberto', class: 'bg-blue-600' },
    em_apuracao: { label: 'Em Apuração', class: 'bg-yellow-600' },
    fechado: { label: 'Fechado', class: 'bg-green-600' }
};

const tipoOperacaoLabels = {
    abastecimento: { label: 'Abastecimento', class: 'bg-green-600', desc: 'Terminal - Recebe Produto' },
    fornecimento: { label: 'Fornecimento', class: 'bg-blue-600', desc: 'Entrega ao Navio' }
};

const classificacaoLabels = {
    conforme: { label: 'Conforme', class: 'bg-green-600', icon: 'CheckCircle' },
    em_observacao: { label: 'Em Observação', class: 'bg-yellow-600', icon: 'AlertTriangle' },
    nao_conforme: { label: 'Não Conforme', class: 'bg-red-600', icon: 'XCircle' }
};

const gradeLabels = {
    VLSFO: 'VLSFO',
    MGO: 'MGO',
    VLSFO_LV: 'VLSFO LV',
    MGO_LV: 'MGO LV'
};

const meses = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' }
];

const ControleCicloPage = () => {
    const [ciclos, setCiclos] = useState([]);
    const [dashboard, setDashboard] = useState(null);
    const [empurradores, setEmpurradores] = useState([]);
    const [balsas, setBalsas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [operacaoDialogOpen, setOperacaoDialogOpen] = useState(false);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [selectedCiclo, setSelectedCiclo] = useState(null);
    const [editingOperacao, setEditingOperacao] = useState(null);
    const [activeTab, setActiveTab] = useState('lista');
    const { hasRole } = useAuth();

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    const [filters, setFilters] = useState({
        embarcacao_tipo: '',
        embarcacao_id: '',
        base_operacional: '',
        grade_produto: '',
        mes: '',
        ano: currentYear.toString(),
        status: '',
        classificacao: ''
    });

    const [formData, setFormData] = useState({
        embarcacao_tipo: '',
        embarcacao_id: '',
        base_operacional: '',
        grade_produto: '',
        mes: currentMonth,
        ano: currentYear,
        estoque_inicial_ciclo: '',
        origem_estoque: 'primeiro_ciclo',
        justificativa_estoque: ''
    });

    const [estoqueAnterior, setEstoqueAnterior] = useState(null);
    const [loadingEstoque, setLoadingEstoque] = useState(false);

    const [operacaoData, setOperacaoData] = useState({
        tipo_operacao: 'abastecimento',
        porto: '',
        navio_atendido: '',
        numero_documento: '',
        data_inicial: '',
        data_final: '',
        volume_recebido: '',
        quantidade_fornecida: '',
        estoque_inicial: '',
        estoque_final: '',
        observacoes: ''
    });

    const [statusData, setStatusData] = useState({
        status: '',
        justificativa: ''
    });

    useEffect(() => {
        fetchBaseData();
    }, []);

    useEffect(() => {
        fetchCiclos();
        fetchDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const fetchBaseData = async () => {
        try {
            const [empRes, balsasRes] = await Promise.all([
                axios.get(`${API}/empurradores`),
                axios.get(`${API}/balsas`)
            ]);
            setEmpurradores(empRes.data);
            setBalsas(balsasRes.data);
        } catch (error) {
            console.error('Erro ao carregar dados base:', error);
        }
    };

    // Buscar estoque anterior quando embarcação e produto são selecionados
    useEffect(() => {
        const fetchEstoqueAnterior = async () => {
            if (!formData.embarcacao_id || !formData.grade_produto) {
                setEstoqueAnterior(null);
                return;
            }
            setLoadingEstoque(true);
            try {
                const res = await axios.get(`${API}/ciclos/estoque-anterior?embarcacao_id=${formData.embarcacao_id}&grade_produto=${formData.grade_produto}`);
                setEstoqueAnterior(res.data);
                // Auto-preencher estoque se há ciclo anterior fechado
                if (res.data.tem_ciclo_anterior) {
                    setFormData(f => ({
                        ...f,
                        estoque_inicial_ciclo: res.data.estoque_disponivel,
                        origem_estoque: 'automatico'
                    }));
                } else {
                    setFormData(f => ({
                        ...f,
                        estoque_inicial_ciclo: '',
                        origem_estoque: 'primeiro_ciclo'
                    }));
                }
            } catch (error) {
                console.error('Erro ao buscar estoque anterior:', error);
            } finally {
                setLoadingEstoque(false);
            }
        };
        if (dialogOpen) fetchEstoqueAnterior();
    }, [formData.embarcacao_id, formData.grade_produto, dialogOpen]);

    const fetchCiclos = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.embarcacao_tipo) params.append('embarcacao_tipo', filters.embarcacao_tipo);
            if (filters.embarcacao_id) params.append('embarcacao_id', filters.embarcacao_id);
            if (filters.base_operacional) params.append('base_operacional', filters.base_operacional);
            if (filters.grade_produto) params.append('grade_produto', filters.grade_produto);
            if (filters.mes) params.append('mes', filters.mes);
            if (filters.ano) params.append('ano', filters.ano);
            if (filters.status) params.append('status', filters.status);
            if (filters.classificacao) params.append('classificacao', filters.classificacao);

            const res = await axios.get(`${API}/ciclos?${params.toString()}`);
            setCiclos(res.data);
        } catch (error) {
            toast.error('Erro ao carregar ciclos');
        } finally {
            setLoading(false);
        }
    };

    const fetchDashboard = async () => {
        try {
            const params = new URLSearchParams();
            if (filters.embarcacao_id) params.append('embarcacao_id', filters.embarcacao_id);
            if (filters.base_operacional) params.append('base_operacional', filters.base_operacional);
            if (filters.grade_produto) params.append('grade_produto', filters.grade_produto);
            if (filters.ano) params.append('ano', filters.ano);
            
            const res = await axios.get(`${API}/ciclos/dashboard?${params.toString()}`);
            setDashboard(res.data);
        } catch (error) {
            console.error('Erro ao carregar dashboard:', error);
        }
    };

    const fetchCicloDetails = async (cicloId) => {
        try {
            const res = await axios.get(`${API}/ciclos/${cicloId}`);
            setSelectedCiclo(res.data);
        } catch (error) {
            toast.error('Erro ao carregar detalhes');
        }
    };

    const handleCreateCiclo = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                estoque_inicial_ciclo: parseFloat(formData.estoque_inicial_ciclo) || 0
            };
            await axios.post(`${API}/ciclos`, payload);
            toast.success('Ciclo criado com sucesso');
            setDialogOpen(false);
            resetForm();
            fetchCiclos();
            fetchDashboard();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao criar ciclo');
        }
    };

    const handleAddOperacao = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                tipo_operacao: operacaoData.tipo_operacao,
                porto: operacaoData.porto,
                navio_atendido: operacaoData.navio_atendido,
                numero_documento: operacaoData.numero_documento,
                data_inicial: operacaoData.data_inicial,
                data_final: operacaoData.data_final,
                volume_recebido: operacaoData.tipo_operacao === 'abastecimento' ? (parseFloat(operacaoData.volume_recebido) || 0) : 0,
                quantidade_fornecida: operacaoData.tipo_operacao === 'fornecimento' ? (parseFloat(operacaoData.quantidade_fornecida) || 0) : 0,
                estoque_inicial: parseFloat(operacaoData.estoque_inicial) || 0,
                estoque_final: parseFloat(operacaoData.estoque_final) || 0,
                observacoes: operacaoData.observacoes
            };

            if (editingOperacao) {
                await axios.put(`${API}/ciclos/${selectedCiclo.id}/operacoes/${editingOperacao.id}`, payload);
                toast.success('Operação atualizada');
            } else {
                await axios.post(`${API}/ciclos/${selectedCiclo.id}/operacoes`, payload);
                toast.success('Operação adicionada');
            }
            
            setOperacaoDialogOpen(false);
            resetOperacaoForm();
            fetchCicloDetails(selectedCiclo.id);
            fetchCiclos();
            fetchDashboard();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar operação');
        }
    };

    const handleDeleteOperacao = async (operacaoId) => {
        if (!window.confirm('Tem certeza que deseja excluir esta operação?')) return;
        
        try {
            await axios.delete(`${API}/ciclos/${selectedCiclo.id}/operacoes/${operacaoId}`);
            toast.success('Operação excluída');
            fetchCicloDetails(selectedCiclo.id);
            fetchCiclos();
            fetchDashboard();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao excluir operação');
        }
    };

    const handleChangeStatus = async (e) => {
        e.preventDefault();
        try {
            await axios.put(`${API}/ciclos/${selectedCiclo.id}/status`, statusData);
            toast.success('Status alterado');
            setStatusDialogOpen(false);
            setStatusData({ status: '', justificativa: '' });
            fetchCicloDetails(selectedCiclo.id);
            fetchCiclos();
            fetchDashboard();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao alterar status');
        }
    };

    const handleExportRelatorio = async (formato) => {
        try {
            const res = await axios.get(`${API}/ciclos/${selectedCiclo.id}/relatorio?formato=${formato}`);
            
            if (formato === 'json') {
                // Download JSON
                const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `ciclo_${selectedCiclo.id}.json`;
                a.click();
            } else {
                // Para PDF/Excel, exportar como CSV
                const data = res.data;
                let csv = `Relatório de Ciclo - ${data.embarcacao}\n`;
                csv += `Período: ${data.periodo}\n`;
                csv += `Grade: ${data.grade_produto}\n`;
                csv += `Status: ${data.status}\n\n`;
                csv += `Total Recebido: ${data.totais.total_recebido}\n`;
                csv += `Total Fornecido: ${data.totais.total_fornecido}\n`;
                csv += `Diferença Acumulada: ${data.totais.diferenca_acumulada}\n`;
                csv += `Percentual: ${data.totais.percentual_consolidado}%\n\n`;
                csv += `Operações:\n`;
                csv += `Porto;Navio;Documento;Data Inicial;Data Final;Volume;Est.Inicial;Est.Final;Diferença;%\n`;
                
                data.operacoes.forEach(op => {
                    csv += `${op.porto};${op.navio_atendido};${op.numero_documento};${op.data_inicial};${op.data_final};${op.volume_recebido};${op.estoque_inicial};${op.estoque_final};${op.diferenca};${op.percentual_diferenca}\n`;
                });
                
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `ciclo_${selectedCiclo.embarcacao_nome}_${meses[selectedCiclo.mes - 1]?.label}_${selectedCiclo.ano}.csv`;
                a.click();
            }
            
            toast.success('Relatório exportado');
        } catch (error) {
            toast.error('Erro ao exportar relatório');
        }
    };

    const handleDeleteCiclo = async (cicloId, forceDelete = false) => {
        const ciclo = ciclos.find(c => c.id === cicloId) || selectedCiclo;
        const temMovimentacao = ciclo && (
            (ciclo.operacoes?.length > 0) || 
            (ciclo.total_recebido > 0) || 
            (ciclo.total_fornecido > 0)
        );
        
        let justificativa = '';
        
        if (temMovimentacao || ciclo?.status === 'fechado') {
            justificativa = window.prompt(
                'Este ciclo possui movimentação ou está fechado.\n\n' +
                'Para excluir, informe uma justificativa obrigatória:'
            );
            if (!justificativa) {
                toast.error('Justificativa é obrigatória para excluir ciclos com movimentação');
                return;
            }
        } else {
            if (!window.confirm('Tem certeza que deseja excluir este ciclo?')) return;
        }
        
        try {
            const url = justificativa 
                ? `${API}/ciclos/${cicloId}?justificativa=${encodeURIComponent(justificativa)}`
                : `${API}/ciclos/${cicloId}`;
            await axios.delete(url);
            toast.success('Ciclo excluído com sucesso');
            setDetailDialogOpen(false);
            setSelectedCiclo(null);
            fetchCiclos();
            fetchDashboard();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao excluir ciclo');
        }
    };

    const openDetails = async (ciclo) => {
        await fetchCicloDetails(ciclo.id);
        setDetailDialogOpen(true);
    };

    const openOperacaoDialog = (operacao = null) => {
        if (operacao) {
            setEditingOperacao(operacao);
            setOperacaoData({
                tipo_operacao: operacao.tipo_operacao || 'abastecimento',
                porto: operacao.porto || '',
                navio_atendido: operacao.navio_atendido || '',
                numero_documento: operacao.numero_documento || '',
                data_inicial: operacao.data_inicial || '',
                data_final: operacao.data_final || '',
                volume_recebido: operacao.volume_recebido?.toString() || '',
                quantidade_fornecida: operacao.quantidade_fornecida?.toString() || '',
                estoque_inicial: operacao.estoque_inicial?.toString() || '',
                estoque_final: operacao.estoque_final?.toString() || '',
                observacoes: operacao.observacoes || ''
            });
        } else {
            setEditingOperacao(null);
            resetOperacaoForm();
        }
        setOperacaoDialogOpen(true);
    };

    const resetForm = () => {
        setFormData({
            embarcacao_tipo: '',
            embarcacao_id: '',
            base_operacional: '',
            grade_produto: '',
            mes: currentMonth,
            ano: currentYear,
            estoque_inicial_ciclo: '',
            origem_estoque: 'primeiro_ciclo',
            justificativa_estoque: ''
        });
        setEstoqueAnterior(null);
    };

    const resetOperacaoForm = () => {
        setOperacaoData({
            tipo_operacao: 'abastecimento',
            porto: '',
            navio_atendido: '',
            numero_documento: '',
            data_inicial: '',
            data_final: '',
            volume_recebido: '',
            quantidade_fornecida: '',
            estoque_inicial: '',
            estoque_final: '',
            observacoes: ''
        });
        setEditingOperacao(null);
    };

    const getEmbarcacoes = (tipo) => {
        if (tipo === 'empurrador') return empurradores;
        if (tipo === 'balsa') return balsas;
        return [];
    };

    const getIndicadorColor = (indicador) => {
        if (indicador === 'verde') return 'bg-green-500';
        if (indicador === 'amarelo') return 'bg-yellow-500';
        return 'bg-red-500';
    };

    const getIndicadorIcon = (indicador) => {
        if (indicador === 'verde') return <CheckCircle className="w-4 h-4 text-green-500" />;
        if (indicador === 'amarelo') return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-2">
                        <Fuel className="w-6 h-6 text-amber-400" />
                        Controle de Ciclo
                    </h1>
                    <p className="text-slate-400 text-sm mt-1">
                        Controle operacional de combustível por embarcação
                    </p>
                </div>
                {hasRole(['admin', 'gestor', 'operador']) && (
                    <Button 
                        onClick={() => setDialogOpen(true)}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid="btn-novo-ciclo"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Novo Ciclo
                    </Button>
                )}
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-slate-800 pb-2">
                <Button 
                    variant={activeTab === 'lista' ? 'default' : 'ghost'}
                    onClick={() => setActiveTab('lista')}
                    className={activeTab === 'lista' ? 'bg-cyan-600' : 'text-slate-400'}
                >
                    <FileText className="w-4 h-4 mr-2" />
                    Ciclos
                </Button>
                <Button 
                    variant={activeTab === 'dashboard' ? 'default' : 'ghost'}
                    onClick={() => setActiveTab('dashboard')}
                    className={activeTab === 'dashboard' ? 'bg-cyan-600' : 'text-slate-400'}
                >
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Dashboard
                </Button>
            </div>

            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && dashboard && (
                <div className="space-y-6">
                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        <Card className="bg-slate-900 border-slate-800">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-bold text-white">{dashboard.total_ciclos}</p>
                                <p className="text-slate-400 text-sm">Total Ciclos</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-900 border-slate-800">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-bold text-blue-400">{dashboard.ciclos_abertos}</p>
                                <p className="text-slate-400 text-sm">Em Aberto</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-900 border-slate-800">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-bold text-yellow-400">{dashboard.ciclos_apuracao}</p>
                                <p className="text-slate-400 text-sm">Em Apuração</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-900 border-slate-800">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-bold text-green-400">{dashboard.ciclos_fechados}</p>
                                <p className="text-slate-400 text-sm">Fechados</p>
                            </CardContent>
                        </Card>
                        <Card className={`bg-slate-900 border-2 ${dashboard.indicador_geral === 'verde' ? 'border-green-500' : dashboard.indicador_geral === 'amarelo' ? 'border-yellow-500' : 'border-red-500'}`}>
                            <CardContent className="p-4 text-center">
                                <p className={`text-3xl font-bold ${dashboard.indicador_geral === 'verde' ? 'text-green-400' : dashboard.indicador_geral === 'amarelo' ? 'text-yellow-400' : 'text-red-400'}`}>
                                    {dashboard.percentual_geral?.toFixed(3)}%
                                </p>
                                <p className="text-slate-400 text-sm">% Geral</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Totais */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-slate-400">Total Recebido</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-2xl font-bold text-white">{dashboard.total_recebido?.toLocaleString('pt-BR')} L</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-slate-400">Total Fornecido</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-2xl font-bold text-blue-400">{dashboard.total_fornecido?.toLocaleString('pt-BR') || dashboard.total_movimentado?.toLocaleString('pt-BR')} L</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-slate-400">Diferença Total</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className={`text-2xl font-bold ${dashboard.diferenca_total >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {dashboard.diferenca_total?.toLocaleString('pt-BR')} L
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Por Grade */}
                    <Card className="bg-slate-900 border-slate-800">
                        <CardHeader>
                            <CardTitle className="text-white">Resumo por Grade de Produto</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {Object.entries(dashboard.por_grade || {}).map(([grade, data]) => (
                                    <div key={grade} className={`p-4 rounded border-l-4 bg-slate-800/50 ${getIndicadorColor(data.indicador).replace('bg-', 'border-')}`}>
                                        <h4 className="text-white font-bold mb-2">{gradeLabels[grade] || grade}</h4>
                                        <div className="space-y-1 text-sm">
                                            <p className="text-slate-400">Ciclos: <span className="text-white">{data.total_ciclos}</span></p>
                                            <p className="text-slate-400">Recebido: <span className="text-white">{data.total_recebido?.toLocaleString('pt-BR')} L</span></p>
                                            <p className="text-slate-400">Diferença: <span className={data.diferenca_acumulada >= 0 ? 'text-green-400' : 'text-red-400'}>{data.diferenca_acumulada?.toFixed(2)} L</span></p>
                                            <p className="text-slate-400">Percentual: <span className={data.indicador === 'verde' ? 'text-green-400' : data.indicador === 'amarelo' ? 'text-yellow-400' : 'text-red-400'}>{data.percentual?.toFixed(3)}%</span></p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Alertas */}
                    {dashboard.ciclos_alerta?.length > 0 && (
                        <Card className="bg-slate-900 border-red-800">
                            <CardHeader>
                                <CardTitle className="text-red-400 flex items-center gap-2">
                                    <AlertTriangle className="w-5 h-5" />
                                    Ciclos com Alerta ({dashboard.ciclos_alerta.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {dashboard.ciclos_alerta.map((alerta, idx) => (
                                        <div key={idx} className="flex justify-between items-center p-3 bg-red-900/20 rounded border border-red-800">
                                            <span className="text-slate-300">
                                                {alerta.grade} - {meses[alerta.mes - 1]?.label}/{alerta.ano}
                                            </span>
                                            <span className="text-red-400 font-bold">
                                                {alerta.percentual?.toFixed(3)}% (limite: ±{alerta.limite}%)
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* Lista Tab */}
            {activeTab === 'lista' && (
                <>
                    {/* Filters */}
                    <Card className="bg-slate-900 border-slate-800">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-4">
                                <Filter className="w-4 h-4 text-slate-400" />
                                <span className="text-slate-400 text-sm font-medium">FILTROS</span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
                                <Select value={filters.embarcacao_tipo || "all"} onValueChange={(v) => setFilters(f => ({ ...f, embarcacao_tipo: v === "all" ? "" : v, embarcacao_id: '' }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Tipo Emb." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        <SelectItem value="empurrador">Empurrador</SelectItem>
                                        <SelectItem value="balsa">Balsa</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={filters.embarcacao_id || "all"} onValueChange={(v) => setFilters(f => ({ ...f, embarcacao_id: v === "all" ? "" : v }))} disabled={!filters.embarcacao_tipo}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Embarcação" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas</SelectItem>
                                        {getEmbarcacoes(filters.embarcacao_tipo).map((emb) => (
                                            <SelectItem key={emb.id} value={emb.id}>{emb.nome}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Input 
                                    placeholder="Base Operacional"
                                    value={filters.base_operacional}
                                    onChange={(e) => setFilters(f => ({ ...f, base_operacional: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                />
                                <Select value={filters.grade_produto || "all"} onValueChange={(v) => setFilters(f => ({ ...f, grade_produto: v === "all" ? "" : v }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Grade" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas</SelectItem>
                                        {Object.entries(gradeLabels).map(([key, label]) => (
                                            <SelectItem key={key} value={key}>{label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select value={filters.mes?.toString() || "all"} onValueChange={(v) => setFilters(f => ({ ...f, mes: v === "all" ? "" : v }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Mês" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        {meses.map((m) => (
                                            <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Input 
                                    type="number"
                                    placeholder="Ano"
                                    value={filters.ano}
                                    onChange={(e) => setFilters(f => ({ ...f, ano: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                />
                                <Select value={filters.status || "all"} onValueChange={(v) => setFilters(f => ({ ...f, status: v === "all" ? "" : v }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        {Object.entries(statusLabels).map(([key, val]) => (
                                            <SelectItem key={key} value={key}>{val.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select value={filters.classificacao || "all"} onValueChange={(v) => setFilters(f => ({ ...f, classificacao: v === "all" ? "" : v }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue placeholder="Classificação" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas</SelectItem>
                                        {Object.entries(classificacaoLabels).map(([key, val]) => (
                                            <SelectItem key={key} value={key}>{val.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Table */}
                    <Card className="bg-slate-900 border-slate-800">
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-slate-800 hover:bg-slate-800/50">
                                            <TableHead className="text-slate-400">Embarcação</TableHead>
                                            <TableHead className="text-slate-400">Grade</TableHead>
                                            <TableHead className="text-slate-400">Período</TableHead>
                                            <TableHead className="text-slate-400">Total Recebido</TableHead>
                                            <TableHead className="text-slate-400">Diferença</TableHead>
                                            <TableHead className="text-slate-400">%</TableHead>
                                            <TableHead className="text-slate-400">Status</TableHead>
                                            <TableHead className="text-slate-400 text-right">Ações</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {loading ? (
                                            <TableRow>
                                                <TableCell colSpan={8} className="text-center text-slate-400 py-8">
                                                    Carregando...
                                                </TableCell>
                                            </TableRow>
                                        ) : ciclos.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={8} className="text-center text-slate-400 py-8">
                                                    Nenhum ciclo encontrado
                                                </TableCell>
                                            </TableRow>
                                        ) : ciclos.map((ciclo) => (
                                            <TableRow key={ciclo.id} className="border-slate-800 hover:bg-slate-800/50">
                                                <TableCell className="text-white">
                                                    <div className="flex items-center gap-2">
                                                        <Ship className="w-4 h-4 text-slate-400" />
                                                        {ciclo.embarcacao_nome}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-amber-400 font-medium">{gradeLabels[ciclo.grade_produto]}</TableCell>
                                                <TableCell className="text-slate-300">{meses[ciclo.mes - 1]?.label}/{ciclo.ano}</TableCell>
                                                <TableCell className="text-white">{ciclo.total_recebido?.toLocaleString('pt-BR')} L</TableCell>
                                                <TableCell className={ciclo.diferenca_acumulada >= 0 ? 'text-green-400' : 'text-red-400'}>
                                                    {ciclo.diferenca_acumulada?.toFixed(2)} L
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        {getIndicadorIcon(ciclo.indicador)}
                                                        <span className={ciclo.indicador === 'verde' ? 'text-green-400' : ciclo.indicador === 'amarelo' ? 'text-yellow-400' : 'text-red-400'}>
                                                            {ciclo.percentual_consolidado?.toFixed(3)}%
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <span className={`px-2 py-1 rounded text-xs text-white ${statusLabels[ciclo.status]?.class || 'bg-slate-600'}`}>
                                                        {statusLabels[ciclo.status]?.label || ciclo.status}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex gap-1 justify-end">
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm" 
                                                            onClick={() => openDetails(ciclo)}
                                                            data-testid={`view-ciclo-${ciclo.id}`}
                                                        >
                                                            <Eye className="w-4 h-4 text-slate-400" />
                                                        </Button>
                                                        {hasRole(['admin']) && (
                                                            <Button 
                                                                variant="ghost" 
                                                                size="sm" 
                                                                onClick={() => handleDeleteCiclo(ciclo.id)}
                                                                className="text-red-400 hover:text-red-300"
                                                                data-testid={`delete-ciclo-${ciclo.id}`}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}

            {/* Dialog: Novo Ciclo */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Fuel className="w-5 h-5 text-amber-400" />
                            Novo Ciclo
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateCiclo} className="space-y-4">
                        <div>
                            <Label className="text-slate-300">Tipo de Embarcação *</Label>
                            <Select value={formData.embarcacao_tipo || undefined} onValueChange={(v) => setFormData(f => ({ ...f, embarcacao_tipo: v, embarcacao_id: '' }))}>
                                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                    <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="empurrador">Empurrador</SelectItem>
                                    <SelectItem value="balsa">Balsa</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-slate-300">Embarcação *</Label>
                            <Select value={formData.embarcacao_id || undefined} onValueChange={(v) => setFormData(f => ({ ...f, embarcacao_id: v }))} disabled={!formData.embarcacao_tipo}>
                                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                    <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                    {getEmbarcacoes(formData.embarcacao_tipo).map((emb) => (
                                        <SelectItem key={emb.id} value={emb.id}>{emb.nome}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-slate-300">Grade de Produto *</Label>
                            <Select value={formData.grade_produto || undefined} onValueChange={(v) => setFormData(f => ({ ...f, grade_produto: v }))}>
                                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                    <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(gradeLabels).map(([key, label]) => (
                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-slate-300">Base Operacional *</Label>
                            <Input 
                                value={formData.base_operacional}
                                onChange={(e) => setFormData(f => ({ ...f, base_operacional: e.target.value }))}
                                className="bg-slate-800 border-slate-700 text-white"
                                placeholder="Ex: Belo Monte do Xingu"
                                required
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-slate-300">Mês *</Label>
                                <Select value={formData.mes?.toString()} onValueChange={(v) => setFormData(f => ({ ...f, mes: parseInt(v) }))}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {meses.map((m) => (
                                            <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-slate-300">Ano *</Label>
                                <Input 
                                    type="number"
                                    value={formData.ano}
                                    onChange={(e) => setFormData(f => ({ ...f, ano: parseInt(e.target.value) }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    min={2020}
                                    max={2030}
                                />
                            </div>
                        </div>

                        {/* Estoque Inicial do Ciclo */}
                        <div className="space-y-2">
                            <Label className="text-slate-300">Estoque Inicial do Ciclo (m³)</Label>
                            {loadingEstoque && (
                                <p className="text-xs text-slate-400">Buscando estoque anterior...</p>
                            )}
                            {estoqueAnterior?.ciclo_pendente && (
                                <div data-testid="alerta-ciclo-pendente" className="bg-red-900/40 border border-red-700 rounded p-2 text-xs text-red-300">
                                    Existe ciclo anterior ({String(estoqueAnterior.ciclo_pendente.mes).padStart(2,'0')}/{estoqueAnterior.ciclo_pendente.ano}) com status "{estoqueAnterior.ciclo_pendente.status === 'em_aberto' ? 'Em Aberto' : 'Em Apuração'}". Feche-o antes de criar um novo.
                                </div>
                            )}
                            {estoqueAnterior?.tem_ciclo_anterior && (
                                <div data-testid="info-estoque-herdado" className="bg-cyan-900/30 border border-cyan-700 rounded p-2 text-xs text-cyan-300">
                                    Estoque herdado do ciclo anterior ({String(estoqueAnterior.ultimo_fechado?.mes).padStart(2,'0')}/{estoqueAnterior.ultimo_fechado?.ano}): <strong>{estoqueAnterior.estoque_disponivel?.toLocaleString('pt-BR')} m³</strong>
                                </div>
                            )}
                            {!estoqueAnterior?.tem_ciclo_anterior && !estoqueAnterior?.ciclo_pendente && formData.embarcacao_id && formData.grade_produto && (
                                <div data-testid="info-primeiro-ciclo" className="bg-amber-900/30 border border-amber-700 rounded p-2 text-xs text-amber-300">
                                    Primeiro ciclo — insira o estoque inicial manualmente.
                                </div>
                            )}
                            <Input
                                data-testid="estoque-inicial-input"
                                type="number"
                                step="0.01"
                                value={formData.estoque_inicial_ciclo}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFormData(f => ({
                                        ...f,
                                        estoque_inicial_ciclo: val,
                                        origem_estoque: estoqueAnterior?.tem_ciclo_anterior
                                            ? (parseFloat(val) !== estoqueAnterior.estoque_disponivel ? 'manual' : 'automatico')
                                            : 'primeiro_ciclo'
                                    }));
                                }}
                                className="bg-slate-800 border-slate-700 text-white"
                                placeholder={estoqueAnterior?.tem_ciclo_anterior ? `Herdado: ${estoqueAnterior.estoque_disponivel}` : 'Informe o estoque inicial'}
                            />
                            {formData.origem_estoque === 'automatico' && (
                                <span className="text-xs text-cyan-400">Origem: Automático (ciclo anterior)</span>
                            )}
                            {formData.origem_estoque === 'manual' && (
                                <span className="text-xs text-amber-400">Origem: Manual (divergência do ciclo anterior)</span>
                            )}
                            {formData.origem_estoque === 'primeiro_ciclo' && formData.embarcacao_id && (
                                <span className="text-xs text-slate-400">Origem: Primeiro Ciclo</span>
                            )}
                        </div>

                        {/* Justificativa se divergente */}
                        {formData.origem_estoque === 'manual' && (
                            <div>
                                <Label className="text-red-400">Justificativa da Divergência *</Label>
                                <Textarea
                                    data-testid="justificativa-estoque-input"
                                    value={formData.justificativa_estoque}
                                    onChange={(e) => setFormData(f => ({ ...f, justificativa_estoque: e.target.value }))}
                                    className="bg-slate-800 border-red-700 text-white"
                                    placeholder="Explique a razão da diferença de estoque"
                                    required
                                />
                            </div>
                        )}
                        <div className="flex justify-end gap-2 pt-4">
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-700">
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                                Criar Ciclo
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Dialog: Detalhes do Ciclo */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-5xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Fuel className="w-5 h-5 text-amber-400" />
                            {selectedCiclo?.embarcacao_nome} - {gradeLabels[selectedCiclo?.grade_produto]} - {meses[selectedCiclo?.mes - 1]?.label}/{selectedCiclo?.ano}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedCiclo && (
                        <div className="space-y-6">
                            {/* Base Operacional e Status */}
                            {selectedCiclo.base_operacional && (
                                <div className="flex items-center gap-2 text-slate-300 text-sm">
                                    <MapPin className="w-4 h-4 text-cyan-400" />
                                    <span>Base: <strong>{selectedCiclo.base_operacional}</strong></span>
                                </div>
                            )}
                            
                            {/* Status e Ações */}
                            <div className="flex flex-wrap gap-2 items-center">
                                <span className={`px-3 py-1 rounded text-sm text-white ${statusLabels[selectedCiclo.status]?.class}`}>
                                    {statusLabels[selectedCiclo.status]?.label}
                                </span>
                                {selectedCiclo.classificacao && (
                                    <span className={`px-3 py-1 rounded text-sm text-white flex items-center gap-1 ${classificacaoLabels[selectedCiclo.classificacao]?.class}`}>
                                        {selectedCiclo.classificacao === 'conforme' && <CheckCircle className="w-3 h-3" />}
                                        {selectedCiclo.classificacao === 'em_observacao' && <AlertTriangle className="w-3 h-3" />}
                                        {selectedCiclo.classificacao === 'nao_conforme' && <XCircle className="w-3 h-3" />}
                                        {classificacaoLabels[selectedCiclo.classificacao]?.label}
                                    </span>
                                )}
                                <div className={`px-3 py-1 rounded flex items-center gap-2 ${getIndicadorColor(selectedCiclo.indicador)}`}>
                                    <span className="text-white text-sm">{selectedCiclo.percentual_consolidado?.toFixed(3)}%</span>
                                </div>
                                <span className="text-slate-400 text-sm">Limite: ±{selectedCiclo.limite_tolerancia}%</span>
                                
                                <div className="flex gap-2 ml-auto">
                                    {hasRole(['admin', 'gestor', 'operador']) && selectedCiclo.status !== 'fechado' && (
                                        <Button 
                                            size="sm" 
                                            onClick={() => openOperacaoDialog()}
                                            className="bg-amber-600 hover:bg-amber-700"
                                        >
                                            <Plus className="w-4 h-4 mr-1" />
                                            Operação
                                        </Button>
                                    )}
                                    {hasRole(['admin', 'gestor']) && (
                                        <Button 
                                            size="sm" 
                                            variant="outline"
                                            onClick={() => { setStatusData({ status: '', justificativa: '' }); setStatusDialogOpen(true); }}
                                            className="border-slate-700"
                                        >
                                            {selectedCiclo.status === 'fechado' ? <Unlock className="w-4 h-4 mr-1" /> : <Lock className="w-4 h-4 mr-1" />}
                                            {selectedCiclo.status === 'fechado' ? 'Reabrir' : 'Alterar Status'}
                                        </Button>
                                    )}
                                    <Button 
                                        size="sm" 
                                        variant="outline"
                                        onClick={() => handleExportRelatorio('csv')}
                                        className="border-slate-700"
                                    >
                                        <Download className="w-4 h-4 mr-1" />
                                        Exportar
                                    </Button>
                                </div>
                            </div>

                            {/* Totais */}
                            <div className="grid grid-cols-2 md:grid-cols-7 gap-4 bg-slate-800/50 p-4 rounded">
                                <div>
                                    <p className="text-slate-400 text-xs">Saldo (Est. Inicial)</p>
                                    <p className="text-cyan-400 font-bold">{selectedCiclo.estoque_inicial_ciclo?.toLocaleString('pt-BR')} m³</p>
                                    <span className="text-[10px] text-slate-500">
                                        {selectedCiclo.origem_estoque === 'automatico' ? 'Herdado' : selectedCiclo.origem_estoque === 'manual' ? 'Manual' : '1º Ciclo'}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-slate-400 text-xs">Total Movimentado</p>
                                    <p className="text-blue-400 font-bold">{selectedCiclo.total_fornecido?.toLocaleString('pt-BR')} m³</p>
                                </div>
                                <div>
                                    <p className="text-slate-400 text-xs">Remanescente Teórico</p>
                                    <p className="text-amber-400 font-bold">{selectedCiclo.remanescente_teorico?.toLocaleString('pt-BR')} m³</p>
                                </div>
                                <div>
                                    <p className="text-slate-400 text-xs">Remanescente Real</p>
                                    <p className="text-white font-bold">{selectedCiclo.saldo_atual?.toLocaleString('pt-BR')} m³</p>
                                </div>
                                <div>
                                    <p className="text-slate-400 text-xs">Diferença</p>
                                    <p className={`font-bold ${selectedCiclo.diferenca_acumulada >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        {selectedCiclo.diferenca_acumulada?.toLocaleString('pt-BR')} m³
                                    </p>
                                </div>
                                <div>
                                    <p className="text-slate-400 text-xs">% (Dif/Saldo)</p>
                                    <p className={`font-bold ${selectedCiclo.indicador === 'verde' ? 'text-green-400' : selectedCiclo.indicador === 'amarelo' ? 'text-yellow-400' : 'text-red-400'}`}>
                                        {selectedCiclo.percentual_consolidado?.toFixed(2)}%
                                    </p>
                                </div>
                                <div>
                                    <p className="text-slate-400 text-xs">
                                        {selectedCiclo.status === 'fechado' ? 'Est. Final Apurado' : 'Total Recebido'}
                                    </p>
                                    {selectedCiclo.status === 'fechado' ? (
                                        <p className="text-amber-400 font-bold">{selectedCiclo.estoque_final_apurado?.toLocaleString('pt-BR')} m³</p>
                                    ) : (
                                        <p className="text-green-400 font-bold">{selectedCiclo.total_recebido?.toLocaleString('pt-BR')} m³</p>
                                    )}
                                </div>
                            </div>

                            {/* Operações */}
                            <div>
                                <h4 className="text-white font-medium mb-3">Operações ({selectedCiclo.operacoes?.length || 0})</h4>
                                {selectedCiclo.operacoes?.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="border-slate-800">
                                                    <TableHead className="text-slate-400">Tipo</TableHead>
                                                    <TableHead className="text-slate-400">Porto</TableHead>
                                                    <TableHead className="text-slate-400">Navio</TableHead>
                                                    <TableHead className="text-slate-400">NF</TableHead>
                                                    <TableHead className="text-slate-400">Período</TableHead>
                                                    <TableHead className="text-slate-400 text-right">Recebido</TableHead>
                                                    <TableHead className="text-slate-400 text-right">Fornecido</TableHead>
                                                    <TableHead className="text-slate-400 text-right">INº</TableHead>
                                                    <TableHead className="text-slate-400 text-right">KNº</TableHead>
                                                    <TableHead className="text-slate-400 text-right">Dif.</TableHead>
                                                    <TableHead className="text-slate-400 text-right">%</TableHead>
                                                    {selectedCiclo.status !== 'fechado' && <TableHead className="text-slate-400"></TableHead>}
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {selectedCiclo.operacoes.map((op) => (
                                                    <TableRow key={op.id} className="border-slate-800">
                                                        <TableCell>
                                                            <span className={`px-2 py-0.5 rounded text-xs text-white ${tipoOperacaoLabels[op.tipo_operacao]?.class || 'bg-slate-600'}`}>
                                                                {tipoOperacaoLabels[op.tipo_operacao]?.label || op.tipo_operacao}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell className="text-white">{op.porto}</TableCell>
                                                        <TableCell className="text-slate-300">{op.navio_atendido}</TableCell>
                                                        <TableCell className="text-slate-300 font-mono text-xs">{op.numero_documento}</TableCell>
                                                        <TableCell className="text-slate-300 text-xs">{op.data_inicial} a {op.data_final}</TableCell>
                                                        <TableCell className="text-green-400 text-right font-medium">
                                                            {op.tipo_operacao === 'abastecimento' ? op.volume_recebido?.toLocaleString('pt-BR') : '-'}
                                                        </TableCell>
                                                        <TableCell className="text-blue-400 text-right font-medium">
                                                            {op.tipo_operacao === 'fornecimento' ? op.quantidade_fornecida?.toLocaleString('pt-BR') : '-'}
                                                        </TableCell>
                                                        <TableCell className="text-slate-300 text-right">{op.estoque_inicial?.toLocaleString('pt-BR')}</TableCell>
                                                        <TableCell className="text-slate-300 text-right">{op.estoque_final?.toLocaleString('pt-BR')}</TableCell>
                                                        <TableCell className={`text-right font-mono ${op.tipo_operacao !== 'abastecimento' ? 'text-slate-500' : (op.diferenca === 0 ? 'text-slate-400' : op.diferenca > 0 ? 'text-emerald-400' : 'text-red-400')}`}>
                                                            {op.tipo_operacao === 'abastecimento' ? op.diferenca?.toFixed(2) : '-'}
                                                        </TableCell>
                                                        {(() => {
                                                            if (op.tipo_operacao !== 'abastecimento') {
                                                                return (
                                                                    <TableCell className="text-right text-slate-500">-</TableCell>
                                                                );
                                                            }
                                                            const limite = selectedCiclo.limite_tolerancia || 0.5;
                                                            const abs = Math.abs(op.percentual_diferenca || 0);
                                                            let cor = 'text-emerald-400';
                                                            let bg = '';
                                                            let icone = '';
                                                            if (abs === 0 && op.percentual_diferenca === 0) {
                                                                // 1o abastecimento (sem calculo)
                                                                cor = 'text-slate-500';
                                                            } else if (abs <= limite * 0.7) {
                                                                cor = 'text-emerald-400';
                                                                icone = '●';
                                                            } else if (abs <= limite) {
                                                                cor = 'text-amber-400';
                                                                bg = 'bg-amber-950/20';
                                                                icone = '●';
                                                            } else {
                                                                cor = 'text-red-400';
                                                                bg = 'bg-red-950/30';
                                                                icone = '▲';
                                                            }
                                                            return (
                                                                <TableCell className={`text-right font-mono ${cor} ${bg}`}>
                                                                    {icone && <span className="mr-1 text-[10px]">{icone}</span>}
                                                                    {op.percentual_diferenca?.toFixed(3)}%
                                                                </TableCell>
                                                            );
                                                        })()}
                                                        {selectedCiclo.status !== 'fechado' && (
                                                            <TableCell>
                                                                <div className="flex gap-1">
                                                                    <Button variant="ghost" size="sm" onClick={() => openOperacaoDialog(op)}>
                                                                        <Edit className="w-3 h-3" />
                                                                    </Button>
                                                                    {hasRole(['admin']) && (
                                                                        <Button variant="ghost" size="sm" onClick={() => handleDeleteOperacao(op.id)}>
                                                                            <Trash2 className="w-3 h-3 text-red-400" />
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                ) : (
                                    <p className="text-slate-500 text-sm text-center py-8">Nenhuma operação registrada</p>
                                )}
                            </div>

                            {/* Fechamento */}
                            {selectedCiclo.status === 'fechado' && selectedCiclo.fechado_por_nome && (
                                <div className={`p-4 rounded border ${
                                    selectedCiclo.classificacao === 'conforme' ? 'bg-green-900/20 border-green-800' :
                                    selectedCiclo.classificacao === 'em_observacao' ? 'bg-yellow-900/20 border-yellow-800' :
                                    'bg-red-900/20 border-red-800'
                                }`}>
                                    <h4 className={`font-medium mb-2 flex items-center gap-2 ${
                                        selectedCiclo.classificacao === 'conforme' ? 'text-green-400' :
                                        selectedCiclo.classificacao === 'em_observacao' ? 'text-yellow-400' :
                                        'text-red-400'
                                    }`}>
                                        {selectedCiclo.classificacao === 'conforme' && <CheckCircle className="w-4 h-4" />}
                                        {selectedCiclo.classificacao === 'em_observacao' && <AlertTriangle className="w-4 h-4" />}
                                        {selectedCiclo.classificacao === 'nao_conforme' && <XCircle className="w-4 h-4" />}
                                        Ciclo Fechado - {classificacaoLabels[selectedCiclo.classificacao]?.label || 'N/A'}
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                        <p className="text-slate-300">Fechado por: <strong>{selectedCiclo.fechado_por_nome}</strong></p>
                                        <p className="text-slate-300">Data: <strong>{selectedCiclo.data_fechamento}</strong></p>
                                    </div>
                                    {selectedCiclo.justificativa_fechamento && (
                                        <p className="text-slate-300 text-sm mt-2 p-2 bg-slate-800/50 rounded">
                                            <strong>Justificativa:</strong> {selectedCiclo.justificativa_fechamento}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Dialog: Operação */}
            <Dialog open={operacaoDialogOpen} onOpenChange={setOperacaoDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-white">
                            {editingOperacao ? 'Editar Operação' : 'Nova Operação'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddOperacao} className="space-y-4">
                        {/* Tipo de Operação */}
                        <div className="p-3 bg-slate-800/50 rounded border border-slate-700">
                            <Label className="text-slate-300 mb-2 block">Tipo de Operação *</Label>
                            <div className="grid grid-cols-2 gap-4">
                                <Button
                                    type="button"
                                    variant={operacaoData.tipo_operacao === 'abastecimento' ? 'default' : 'outline'}
                                    className={operacaoData.tipo_operacao === 'abastecimento' ? 'bg-green-600 hover:bg-green-700' : 'border-slate-600'}
                                    onClick={() => setOperacaoData(o => ({ ...o, tipo_operacao: 'abastecimento' }))}
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Abastecimento
                                    <span className="text-xs ml-2 opacity-70">(Terminal)</span>
                                </Button>
                                <Button
                                    type="button"
                                    variant={operacaoData.tipo_operacao === 'fornecimento' ? 'default' : 'outline'}
                                    className={operacaoData.tipo_operacao === 'fornecimento' ? 'bg-blue-600 hover:bg-blue-700' : 'border-slate-600'}
                                    onClick={() => setOperacaoData(o => ({ ...o, tipo_operacao: 'fornecimento' }))}
                                >
                                    <Ship className="w-4 h-4 mr-2" />
                                    Fornecimento
                                    <span className="text-xs ml-2 opacity-70">(Navio)</span>
                                </Button>
                            </div>
                            <p className="text-slate-500 text-xs mt-2">
                                {operacaoData.tipo_operacao === 'abastecimento' 
                                    ? 'A balsa RECEBE produto do terminal' 
                                    : 'A balsa ENTREGA produto ao navio'
                                }
                            </p>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-slate-300">Porto *</Label>
                                <Input 
                                    value={operacaoData.porto}
                                    onChange={(e) => setOperacaoData(o => ({ ...o, porto: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    placeholder={operacaoData.tipo_operacao === 'abastecimento' ? 'Ex: Terminal de Miramar' : 'Ex: Outeiro'}
                                    required
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">Navio Atendido *</Label>
                                <Input 
                                    value={operacaoData.navio_atendido}
                                    onChange={(e) => setOperacaoData(o => ({ ...o, navio_atendido: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    required
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">Nº Documento / NF *</Label>
                                <Input 
                                    value={operacaoData.numero_documento}
                                    onChange={(e) => setOperacaoData(o => ({ ...o, numero_documento: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    required
                                />
                            </div>
                            {operacaoData.tipo_operacao === 'abastecimento' && (
                                <div>
                                    <Label className="text-slate-300">Volume Recebido (L) *</Label>
                                    <Input 
                                        type="number"
                                        step="0.0001"
                                        value={operacaoData.volume_recebido}
                                        onChange={(e) => setOperacaoData(o => ({ ...o, volume_recebido: e.target.value }))}
                                        className="bg-slate-800 border-slate-700 text-white"
                                        required={operacaoData.tipo_operacao === 'abastecimento'}
                                    />
                                </div>
                            )}
                            {operacaoData.tipo_operacao === 'fornecimento' && (
                                <div>
                                    <Label className="text-slate-300">Quantidade Fornecida (L) *</Label>
                                    <Input 
                                        type="number"
                                        step="0.0001"
                                        value={operacaoData.quantidade_fornecida}
                                        onChange={(e) => setOperacaoData(o => ({ ...o, quantidade_fornecida: e.target.value }))}
                                        className="bg-slate-800 border-slate-700 text-white"
                                        required={operacaoData.tipo_operacao === 'fornecimento'}
                                    />
                                </div>
                            )}
                            <div>
                                <Label className="text-slate-300">Data Inicial *</Label>
                                <Input 
                                    type="date"
                                    value={operacaoData.data_inicial}
                                    onChange={(e) => setOperacaoData(o => ({ ...o, data_inicial: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    required
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">Data Final *</Label>
                                <Input 
                                    type="date"
                                    value={operacaoData.data_final}
                                    onChange={(e) => setOperacaoData(o => ({ ...o, data_final: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    required
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">Estoque Inicial - INº (L) *</Label>
                                <Input 
                                    type="number"
                                    step="0.0001"
                                    value={operacaoData.estoque_inicial}
                                    onChange={(e) => setOperacaoData(o => ({ ...o, estoque_inicial: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    required
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300">Estoque Final - KNº (L) *</Label>
                                <Input 
                                    type="number"
                                    step="0.0001"
                                    value={operacaoData.estoque_final}
                                    onChange={(e) => setOperacaoData(o => ({ ...o, estoque_final: e.target.value }))}
                                    className="bg-slate-800 border-slate-700 text-white"
                                    required
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-slate-300">Observações</Label>
                            <Textarea 
                                value={operacaoData.observacoes}
                                onChange={(e) => setOperacaoData(o => ({ ...o, observacoes: e.target.value }))}
                                className="bg-slate-800 border-slate-700 text-white"
                                rows={2}
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                            <Button type="button" variant="outline" onClick={() => setOperacaoDialogOpen(false)} className="border-slate-700">
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                                {editingOperacao ? 'Salvar' : 'Adicionar'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Dialog: Alterar Status */}
            <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800">
                    <DialogHeader>
                        <DialogTitle className="text-white">Alterar Status do Ciclo</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleChangeStatus} className="space-y-4">
                        <div>
                            <Label className="text-slate-300">Novo Status *</Label>
                            <Select value={statusData.status || undefined} onValueChange={(v) => setStatusData(s => ({ ...s, status: v }))}>
                                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                    <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(statusLabels).map(([key, val]) => (
                                        <SelectItem key={key} value={key}>{val.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {statusData.status === 'fechado' && selectedCiclo && selectedCiclo.percentual_consolidado < 0 && Math.abs(selectedCiclo.percentual_consolidado) > selectedCiclo.limite_tolerancia && (
                            <div className="bg-red-900/20 p-3 rounded border border-red-800">
                                <p className="text-red-400 text-sm flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4" />
                                    Diferenca negativa ({selectedCiclo.percentual_consolidado?.toFixed(3)}%) excede o limite (±{selectedCiclo.limite_tolerancia}%). Justificativa obrigatoria.
                                </p>
                            </div>
                        )}
                        {statusData.status === 'fechado' && selectedCiclo && selectedCiclo.percentual_consolidado >= 0 && (
                            <div className="bg-green-900/20 p-3 rounded border border-green-800">
                                <p className="text-green-400 text-sm flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4" />
                                    Remanescente real superior ao teorico. Nao ha descumprimento contratual.
                                </p>
                            </div>
                        )}
                        <div>
                            <Label className="text-slate-300">Justificativa {statusData.status === 'fechado' && selectedCiclo?.percentual_consolidado < 0 && Math.abs(selectedCiclo?.percentual_consolidado || 0) > (selectedCiclo?.limite_tolerancia || 0.5) ? '*' : ''}</Label>
                            <Textarea 
                                value={statusData.justificativa}
                                onChange={(e) => setStatusData(s => ({ ...s, justificativa: e.target.value }))}
                                className="bg-slate-800 border-slate-700 text-white"
                                rows={3}
                                placeholder="Motivo da alteração de status..."
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => setStatusDialogOpen(false)} className="border-slate-700">
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700" disabled={!statusData.status}>
                                Confirmar
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ControleCicloPage;
